import { useCallback, useRef, useState } from 'react';
import {
    CategoryPublication,
    getCategoriesPublications,
    PaginatedCategoryPublication,
} from '../../api/category';
import { getCategoryFlowErrorMessage } from '../../api/problemJson';
import { Option } from '../../types/option';

const PUBLICATIONS_LOAD_ERROR_MESSAGE =
    'Impossible de charger les publications de cette catégorie.';

type CategoryPublicationsState = {
    page: number;
    total: number;
    publications: CategoryPublication[];
};
export function useCategoryPublications() {
    const [categories, setCategories] = useState<Map<number, CategoryPublicationsState>>(new Map());
    const categoriesRef = useRef(categories);
    categoriesRef.current = categories;

    const [loadingCategoryIds, setLoadingCategoryIds] = useState<ReadonlySet<number>>(new Set());
    const [publicationsLoadErrors, setPublicationsLoadErrors] = useState<
        ReadonlyMap<number, string>
    >(new Map());

    const startLoading = useCallback((categoryId: number) => {
        setLoadingCategoryIds(prev => new Set(prev).add(categoryId));
    }, []);

    const stopLoading = useCallback((categoryId: number) => {
        setLoadingCategoryIds(prev => {
            const next = new Set(prev);
            next.delete(categoryId);

            return next;
        });
    }, []);

    const loadPage = useCallback(
        async (categoryId: number) => {
            const current = categoriesRef.current.get(categoryId);

            if (loadingCategoryIds.has(categoryId)) {
                return;
            }

            const nextPage = (current?.page ?? 0) + 1;

            startLoading(categoryId);

            try {
                const result: PaginatedCategoryPublication = await getCategoriesPublications(
                    categoryId,
                    nextPage,
                );

                setCategories(prev => {
                    const next = new Map(prev);

                    next.set(categoryId, {
                        page: nextPage,
                        total: result.total,
                        publications: [...(current?.publications ?? []), ...result.publications],
                    });

                    return next;
                });

                setPublicationsLoadErrors(prev => {
                    const next = new Map(prev);
                    next.delete(categoryId);

                    return next;
                });
            } catch (error) {
                setPublicationsLoadErrors(prev =>
                    new Map(prev).set(
                        categoryId,
                        getCategoryFlowErrorMessage(error, PUBLICATIONS_LOAD_ERROR_MESSAGE),
                    ),
                );
            } finally {
                stopLoading(categoryId);
            }
        },
        [loadingCategoryIds, startLoading, stopLoading],
    );

    const refreshCategory = useCallback(
        async (categoryId: number) => {
            startLoading(categoryId);

            try {
                const result = await getCategoriesPublications(categoryId, 1);

                setCategories(prev => {
                    const next = new Map(prev);

                    next.set(categoryId, {
                        page: 1,
                        total: result.total,
                        publications: result.publications,
                    });

                    return next;
                });
            } finally {
                stopLoading(categoryId);
            }
        },
        [startLoading, stopLoading],
    );

    const hasMore = useCallback((categoryId: number): boolean => {
        const category = categoriesRef.current.get(categoryId);

        if (!category) {
            return true;
        }

        return category.page < category.total;
    }, []);

    const getPublications = useCallback(
        (categoryId: number): CategoryPublication[] =>
            categories.get(categoryId)?.publications ?? [],
        [categories],
    );

    const getPublicationsLoadError = useCallback(
        (categoryId: number): Option<string> => {
            const message = publicationsLoadErrors.get(categoryId);
            return message ? Option.some(message) : Option.none();
        },
        [publicationsLoadErrors],
    );

    const isCategoryLoading = useCallback(
        (categoryId: number) => loadingCategoryIds.has(categoryId),
        [loadingCategoryIds],
    );

    return {
        loadPage,
        refreshCategory,
        hasMore,
        getPublications,
        getPublicationsLoadError,
        isCategoryLoading,
    };
}
